import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Modal,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
} from "@mui/material";
import { Add, Delete, ExpandMore, Edit } from "@mui/icons-material";
import styled from "@emotion/styled";
import { tableCellClasses } from "@mui/material/TableCell";
import ContentBox from "../../components/ui/ContentBox";
import ButtonSquare from "../../components/ui/ButtonSquare";
import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import { useAuthContext } from "../../hooks/useAuthContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Formik, Field, Form, ErrorMessage, FieldArray } from "formik";
import FormikErrorText from "../../components/ui/FormikErrorText";
import * as Yup from "yup";
import { modalStyleOverflow } from "../../theme/ModalStyle";

// ─── Styled components ────────────────────────────────────────
const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "white",
        paddingTop: "8px",
        paddingBottom: "8px",
        letterSpacing: "1px",
        color: "#4B465C",
        fontSize: 13,
        fontWeight: 500,
    },
    [`&.${tableCellClasses.body}`]: {
        background: "white",
        fontSize: 14,
        color: "#666D83",
        fontWeight: 500,
    },
}));

const StyledTableRow = styled(TableRow)(() => ({
    "&:last-child td, &:last-child th": { border: 0 },
    ":hover": { filter: "brightness(0.95)" },
    cursor: "pointer",
}));

const StyledTable = styled(Table)({ borderRadius: "0px" });

// ─── Helper: group levels by age range ────────────────────────
function groupLevelsByAge(levels) {
    const map = {};
    levels.forEach((level) => {
        const key = `${level.age_min ?? "null"}-${level.age_max ?? "null"}`;
        if (!map[key]) {
            map[key] = {
                key,
                age_min: level.age_min,
                age_max: level.age_max,
                levels: [],
            };
        }
        map[key].levels.push(level);
    });
    return Object.values(map);
}

// ─── Helper: extract shared questions from a group of levels ──
function extractQuestionsFromGroup(group) {
    const questionMap = {};
    group.levels.forEach((level) => {
        (level.scenarios || []).forEach((sc) => {
            if (sc.question && !questionMap[sc.question.id]) {
                questionMap[sc.question.id] = {
                    id: sc.question.id,
                    questionText: sc.question.value,
                    answers: (sc.question.answers || []).map((a) => ({
                        id: a.id,
                        text: a.value,
                        stopQuestionnaire: a.skip_next || false,
                    })),
                };
            }
        });
    });
    return Object.values(questionMap);
}

// ─── Helper: build scenarios from grouped levels ──────────────
function buildScenariosFromGroup(group, questions) {
    return group.levels.map((level) => {
        const answers = questions.map((q) => {
            const sc = (level.scenarios || []).find(
                (s) => s.question_id === q.id
            );
            if (sc && sc.answer) {
                return sc.answer.value;
            }
            return "";
        });
        return {
            levelId: level.id,
            scenarioLevel: level.name,
            answers,
        };
    });
}

// ─── Should show question (stop-questionnaire logic) ──────────
function shouldShowQuestion(questions, questionIndex, currentAnswers) {
    if (questionIndex === 0) return true;
    for (let i = 0; i < questionIndex; i++) {
        const answerText = currentAnswers?.[i];
        if (!answerText) continue;
        const question = questions[i];
        if (!question?.answers) continue;
        const selectedAnswer = question.answers.find((a) => a.text === answerText);
        if (selectedAnswer?.stopQuestionnaire) return false;
    }
    return true;
}

// ─── Validation schemas ───────────────────────────────────────
const createValidationSchema = Yup.object().shape({
    ageGroups: Yup.array().of(
        Yup.object().shape({
            minAge: Yup.number().required("Required").min(0, "Min Age cannot be negative"),
            maxAge: Yup.number().required("Required").moreThan(Yup.ref("minAge"), "Max Age must be greater than Min Age"),
            questions: Yup.array().of(
                Yup.object().shape({
                    questionText: Yup.string().required("Required"),
                    answers: Yup.array()
                        .of(
                            Yup.object().shape({
                                text: Yup.string().required("Required"),
                                stopQuestionnaire: Yup.boolean().default(false),
                            })
                        )
                        .test("at-least-one-continue", "At least one answer must allow continuation", function (answers) {
                            if (!answers) return false;
                            return answers.some((answer) => !answer.stopQuestionnaire);
                        }),
                })
            ),
        })
    ),
    scenarios: Yup.array().of(
        Yup.object().shape({
            ageGroupIndex: Yup.number().required("Required"),
            scenarioLevel: Yup.string().required("Required"),
        })
    ),
});

const editGroupValidationSchema = Yup.object().shape({
    ageGroup: Yup.object().shape({
        minAge: Yup.number().required("Required").min(0, "Min Age cannot be negative"),
        maxAge: Yup.number().required("Required").moreThan(Yup.ref("minAge"), "Max Age must be greater than Min Age"),
    }),
    questions: Yup.array().of(
        Yup.object().shape({
            questionText: Yup.string().required("Required"),
            answers: Yup.array()
                .of(
                    Yup.object().shape({
                        text: Yup.string().required("Required"),
                        stopQuestionnaire: Yup.boolean().default(false),
                    })
                )
                .test("at-least-one-continue", "At least one answer must allow continuation", function (answers) {
                    if (!answers) return false;
                    return answers.some((answer) => !answer.stopQuestionnaire);
                }),
        })
    ),
    scenarios: Yup.array()
        .of(
            Yup.object().shape({
                scenarioLevel: Yup.string().required("Level name is required"),
            })
        )
        .min(1, "At least one scenario is required"),
});

// ═══════════════════════════════════════════════════════════════
// ─── Component ────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════
function Levels() {
    const { user } = useAuthContext();
    const access_token = user?.access_token;
    const [levels, setLevels] = useState([]);
    const [openAddLevel, setOpenAddLevel] = useState(false);
    const [openEditGroup, setOpenEditGroup] = useState({ open: false, group: null });
    const [openDeleteLevel, setOpenDeleteLevel] = useState({ open: false, data: null });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [change, setChange] = useState(false);

    const groups = useMemo(() => groupLevelsByAge(levels), [levels]);

    useEffect(() => {
        document.title = "Moody admin - Levels";
    }, []);

    // ─── Fetch levels ─────────────────────────────────────────
    useEffect(() => {
        setLoading(true);
        Api.get(Urls.GET_LEVELS, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` },
            params: { ...(search && { search }) },
        })
            .then((res) => {
                setLevels(res?.data?.data);
                setLoading(false);
            })
            .catch((err) => {
                toast.error(err?.response?.data?.message);
                setLoading(false);
            });
    }, [change, search, access_token]);

    // ─── Create handler ───────────────────────────────────────
    const handleSubmitCreate = (values) => {
        Api.post(Urls.CREATE_LEVELS, { data: values }, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` },
        })
            .then(() => {
                toast.success("Levels Created Successfully");
                setChange(!change);
                setOpenAddLevel(false);
            })
            .catch((err) => toast.error(err.response?.data?.message));
    };

    // ─── Edit group handler ───────────────────────────────────
    const handleSubmitEditGroup = (values) => {
        const requestData = {
            data: {
                ageGroup: { minAge: values.ageGroup.minAge, maxAge: values.ageGroup.maxAge },
                existingLevelIds: openEditGroup.group.levels.map((l) => l.id),
                questions: values.questions.map((q) => ({
                    id: q.id || null,
                    questionText: q.questionText,
                    answers: q.answers.map((a) => ({
                        id: a.id || null,
                        text: a.text,
                        stopQuestionnaire: a.stopQuestionnaire || false,
                    })),
                })),
                scenarios: values.scenarios.map((s) => ({
                    levelId: s.levelId || null,
                    scenarioLevel: s.scenarioLevel,
                    answers: s.answers || [],
                })),
            },
        };

        Api.patch(Urls.UPDATE_LEVELS_GROUP, requestData, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` },
        })
            .then(() => {
                toast.success("Level Group Updated Successfully");
                setChange(!change);
                setOpenEditGroup({ open: false, group: null });
            })
            .catch((err) => toast.error(err.response?.data?.message));
    };

    // ─── Delete handler ───────────────────────────────────────
    const handleDeleteLevel = (levelId) => {
        Api.delete(`${Urls.DELETE_LEVELS}/${levelId}`, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` },
        })
            .then(() => {
                toast.success("Level Deleted Successfully");
                setChange(!change);
                setOpenDeleteLevel({ open: false, data: null });
            })
            .catch((err) => toast.error(err.response?.data?.message));
    };

    // ─── Open edit group modal ────────────────────────────────
    const handleOpenEditGroup = (group) => {
        setOpenEditGroup({ open: true, group });
    };

    // ─── Build edit initial values from a group ───────────────
    const buildEditInitialValues = (group) => {
        if (!group) return null;
        const questions = extractQuestionsFromGroup(group);
        const scenarios = buildScenariosFromGroup(group, questions);
        return {
            ageGroup: { minAge: group.age_min ?? 0, maxAge: group.age_max ?? 0 },
            hasQuestions: questions.length > 0,
            questions,
            scenarios,
        };
    };

    // ─── shouldShowQuestion for create modal ──────────────────
    const shouldShowQuestionCreate = (values, ageGroupIndex, questionIndex, currentAnswers) => {
        if (questionIndex === 0) return true;
        const ageGroup = values.ageGroups[ageGroupIndex];
        if (!ageGroup?.questions) return false;
        for (let i = 0; i < questionIndex; i++) {
            const answerText = currentAnswers?.[i];
            if (!answerText) continue;
            const question = ageGroup.questions[i];
            if (!question?.answers) continue;
            const selectedAnswer = question.answers.find(a => a.text === answerText);
            if (selectedAnswer?.stopQuestionnaire) return false;
        }
        return true;
    };

    return (
        <Box display="flex" flexDirection="column" gap="26px" marginTop="26px">
            <ToastContainer />
            <Typography fontSize="22px" fontWeight="500" lineHeight="30px" color="#4B465C">
                Levels
            </Typography>

            <ContentBox>
                <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="16px">
                    <Button sx={{ height: "38px" }} variant="primary_active" onClick={() => setOpenAddLevel(true)}>
                        Create Levels
                    </Button>
                    <TextField value={search} onChange={(e) => setSearch(e.target.value)} type="input" placeholder="Search level" />
                </Box>
            </ContentBox>

            {/* ─── Table: Grouped by age range ──────────────── */}
            <ContentBox>
                {!loading ? (
                    groups.length > 0 ? (
                        <Box display="flex" flexDirection="column" gap={2}>
                            {groups.map((group) => (
                                <Box key={group.key} border="1px solid #DBDADE" borderRadius="8px" overflow="hidden">
                                    {/* Group Header */}
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        px={2}
                                        py={1.5}
                                        bgcolor="#f5f5f7"
                                        borderBottom="1px solid #DBDADE"
                                    >
                                        <Box display="flex" alignItems="center" gap={2}>
                                            <Typography fontWeight="600" color="#4B465C" fontSize="15px">
                                                Age Group: {group.age_min ?? "—"} – {group.age_max ?? "—"}
                                            </Typography>
                                            <Chip
                                                label={`${group.levels.length} level${group.levels.length > 1 ? "s" : ""}`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Edit />}
                                            onClick={() => handleOpenEditGroup(group)}
                                        >
                                            Edit Group
                                        </Button>
                                    </Box>
                                    {/* Levels in this group */}
                                    <TableContainer>
                                        <StyledTable size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <StyledTableCell width="5%" align="left">ID</StyledTableCell>
                                                    <StyledTableCell width="30%" align="left">LEVEL NAME</StyledTableCell>
                                                    <StyledTableCell width="50%" align="left">SCENARIO</StyledTableCell>
                                                    <StyledTableCell width="15%" align="center">ACTION</StyledTableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {group.levels.map((level) => (
                                                    <StyledTableRow key={level.id}>
                                                        <StyledTableCell align="left">
                                                            <Typography fontWeight="400" color="#3890E2" fontSize="15px">
                                                                #{level.id}
                                                            </Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="left">
                                                            {level.name || "No Name"}
                                                        </StyledTableCell>
                                                        <StyledTableCell align="left">
                                                            {level.scenarios && level.scenarios.length > 0 ? (
                                                                <Box display="flex" gap={0.5} flexWrap="wrap">
                                                                    {level.scenarios.map((sc, i) => (
                                                                        <Chip
                                                                            key={i}
                                                                            size="small"
                                                                            label={`${sc.question?.value}: ${sc.answer?.value}`}
                                                                            variant="outlined"
                                                                            sx={{ fontSize: "12px" }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            ) : (
                                                                <Typography color="textSecondary" fontSize="13px">
                                                                    Age-only (no questions)
                                                                </Typography>
                                                            )}
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center">
                                                            <ButtonSquare
                                                                action="delete_red"
                                                                onClick={() => setOpenDeleteLevel({ open: true, data: level })}
                                                            />
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                ))}
                                            </TableBody>
                                        </StyledTable>
                                    </TableContainer>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography textAlign="center">No levels</Typography>
                    )
                ) : (
                    <Box display="flex" justifyContent="center">
                        <CircularProgress />
                    </Box>
                )}
            </ContentBox>

            {/* ═══════════ Modal: Create Levels ═══════════ */}
            <Modal open={openAddLevel} onClose={() => setOpenAddLevel(false)}>
                <Box sx={modalStyleOverflow}>
                    <Box position="absolute" top="2px" right="2px">
                        <ButtonSquare action="close" onClick={() => setOpenAddLevel(false)} />
                    </Box>
                    <Box>
                        <Formik
                            initialValues={{
                                ageGroups: [{ minAge: "", maxAge: "", hasQuestions: false, questions: [] }],
                                scenarios: [],
                            }}
                            validationSchema={createValidationSchema}
                            onSubmit={handleSubmitCreate}
                        >
                            {({ values, errors, setFieldValue }) => (
                                <Form>
                                    <Box marginBottom="26px">
                                        <Typography textAlign="left" fontSize="26px" lineHeight="36px" fontWeight="500" color="#4B465C">
                                            Add Levels
                                        </Typography>
                                    </Box>

                                    {/* ─── Age Groups ──────────────── */}
                                    <FieldArray name="ageGroups">
                                        {({ push, remove }) => (
                                            <Box>
                                                {values.ageGroups.map((group, index) => (
                                                    <Box key={index} border={1} borderColor="grey.300" borderRadius="5px" mb={2}>
                                                        <Accordion defaultExpanded>
                                                            <AccordionSummary expandIcon={<ExpandMore />}>
                                                                <Typography variant="h6">Age Group {index + 1}</Typography>
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                <Box display="flex" gap="16px" flexWrap="wrap" mt={2}>
                                                                    <Box flex={1}>
                                                                        <Typography fontSize="13px" fontWeight="400" color="#4B465C">Min Age</Typography>
                                                                        <Field style={{ width: "100%" }} name={`ageGroups.${index}.minAge`} type="number" as={TextField} autoComplete="off" placeholder="Min Age" inputProps={{ min: 0 }} />
                                                                        <ErrorMessage component={FormikErrorText} name={`ageGroups.${index}.minAge`} />
                                                                    </Box>
                                                                    <Box flex={1}>
                                                                        <Typography fontSize="13px" fontWeight="400" color="#4B465C">Max Age</Typography>
                                                                        <Field style={{ width: "100%" }} name={`ageGroups.${index}.maxAge`} type="number" as={TextField} autoComplete="off" placeholder="Max Age" inputProps={{ min: 0 }} />
                                                                        <ErrorMessage component={FormikErrorText} name={`ageGroups.${index}.maxAge`} />
                                                                    </Box>
                                                                </Box>
                                                                <Field name={`ageGroups.${index}.hasQuestions`}>
                                                                    {({ field }) => (
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    {...field}
                                                                                    checked={field.value}
                                                                                    onChange={(e) => {
                                                                                        setFieldValue(`ageGroups.${index}.hasQuestions`, e.target.checked);
                                                                                        if (e.target.checked) {
                                                                                            if (!group.questions?.length) {
                                                                                                setFieldValue(`ageGroups.${index}.questions`, [{ questionText: "", answers: [{ text: "", stopQuestionnaire: false }] }]);
                                                                                            }
                                                                                        } else {
                                                                                            setFieldValue(`ageGroups.${index}.questions`, []);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            }
                                                                            label="Has Questions"
                                                                        />
                                                                    )}
                                                                </Field>
                                                                {group.hasQuestions && (
                                                                    <QuestionsEditor basePath={`ageGroups.${index}.questions`} questions={group.questions} setFieldValue={setFieldValue} />
                                                                )}
                                                            </AccordionDetails>
                                                        </Accordion>
                                                        <Box display="flex" justifyContent="end" mt={2}>
                                                            <Button size="small" color="error" onClick={() => remove(index)} startIcon={<Delete />} disabled={values.ageGroups.length === 1}>
                                                                Remove Age Group
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                ))}
                                                <Button onClick={() => push({ minAge: "", maxAge: "", hasQuestions: false, questions: [] })} startIcon={<Add />}>
                                                    Add Age Group
                                                </Button>
                                            </Box>
                                        )}
                                    </FieldArray>

                                    {/* ─── Scenarios ───────────────── */}
                                    <FieldArray name="scenarios">
                                        {({ push, remove }) => (
                                            <Box mt={4}>
                                                <Typography variant="h5" gutterBottom>Scenarios</Typography>
                                                {values.scenarios.map((scenario, sIndex) => {
                                                    const ageGroupIdx = scenario.ageGroupIndex;
                                                    const selectedAgeGroup = ageGroupIdx !== "" && ageGroupIdx !== undefined && values.ageGroups[ageGroupIdx] ? values.ageGroups[ageGroupIdx] : null;
                                                    return (
                                                        <Box key={sIndex} border={1} borderColor="grey.300" borderRadius="10px" mt={2}>
                                                            <Accordion defaultExpanded>
                                                                <AccordionSummary expandIcon={<ExpandMore />}>
                                                                    <Typography variant="subtitle1">Scenario {sIndex + 1}</Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails>
                                                                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                                                                        <Field name={`scenarios.${sIndex}.ageGroupIndex`} as={TextField} select label="Select Age Group" fullWidth
                                                                            onChange={(e) => {
                                                                                const selected = e.target.value;
                                                                                setFieldValue(`scenarios.${sIndex}.ageGroupIndex`, selected);
                                                                                const sg = values.ageGroups[selected];
                                                                                setFieldValue(`scenarios.${sIndex}.answers`, sg?.questions ? sg.questions.map(() => "") : []);
                                                                            }}
                                                                        >
                                                                            <MenuItem value="">Select Age Group</MenuItem>
                                                                            {values.ageGroups.map((g, gIdx) => (
                                                                                <MenuItem key={gIdx} value={gIdx}>Age Group {gIdx + 1} ({g.minAge} - {g.maxAge})</MenuItem>
                                                                            ))}
                                                                        </Field>
                                                                        {selectedAgeGroup?.questions?.map((question, qIndex) => {
                                                                            if (!shouldShowQuestionCreate(values, ageGroupIdx, qIndex, scenario.answers || [])) return null;
                                                                            return (
                                                                                <Field key={qIndex} name={`scenarios.${sIndex}.answers.${qIndex}`} as={TextField} select label={question.questionText} fullWidth
                                                                                    value={scenario.answers?.[qIndex] || ""}
                                                                                    onChange={(e) => {
                                                                                        const newAnswers = [...(scenario.answers || [])];
                                                                                        newAnswers[qIndex] = e.target.value;
                                                                                        const selAns = question.answers.find((a) => a.text === e.target.value);
                                                                                        if (selAns?.stopQuestionnaire) {
                                                                                            for (let i = qIndex + 1; i < newAnswers.length; i++) newAnswers[i] = "";
                                                                                        }
                                                                                        setFieldValue(`scenarios.${sIndex}.answers`, newAnswers);
                                                                                    }}
                                                                                >
                                                                                    <MenuItem value="">Select Answer</MenuItem>
                                                                                    {question.answers.map((ans, aIdx) => (
                                                                                        <MenuItem key={aIdx} value={ans.text} style={{ color: ans.stopQuestionnaire ? "#f44336" : "inherit", fontWeight: ans.stopQuestionnaire ? "bold" : "normal" }}>
                                                                                            {ans.text}{ans.stopQuestionnaire && " (Stop)"}
                                                                                        </MenuItem>
                                                                                    ))}
                                                                                </Field>
                                                                            );
                                                                        })}
                                                                        <Field name={`scenarios.${sIndex}.scenarioLevel`} as={TextField} label="Level name" fullWidth />
                                                                        <ErrorMessage name={`scenarios.${sIndex}.scenarioLevel`} component={FormikErrorText} />
                                                                    </Box>
                                                                    <Box display="flex" justifyContent="end">
                                                                        <Button size="small" color="error" onClick={() => remove(sIndex)} startIcon={<Delete />} sx={{ mt: 2 }}>Remove Scenario</Button>
                                                                    </Box>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        </Box>
                                                    );
                                                })}
                                                <Button onClick={() => push({ scenarioLevel: "", ageGroupIndex: "", answers: [] })} startIcon={<Add />}>Add Scenario</Button>
                                            </Box>
                                        )}
                                    </FieldArray>

                                    <Box mt={2}>
                                        <Button type="submit" variant="primary_active" disabled={Object.keys(errors).length > 0}>
                                            Submit Levels and Scenarios
                                        </Button>
                                        {Object.keys(errors).length > 0 && (
                                            <Typography variant="body2" color="error" mt={1}>Please fix the validation errors above before submitting.</Typography>
                                        )}
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    </Box>
                </Box>
            </Modal>

            {/* ═══════════ Modal: Edit Level Group ═══════════ */}
            <Modal
                open={openEditGroup.open}
                onClose={() => setOpenEditGroup({ open: false, group: null })}
            >
                <Box sx={modalStyleOverflow}>
                    <Box position="absolute" top="2px" right="2px">
                        <ButtonSquare action="close" onClick={() => setOpenEditGroup({ open: false, group: null })} />
                    </Box>
                    <Box>
                        {openEditGroup.group && (() => {
                            const editInit = buildEditInitialValues(openEditGroup.group);
                            if (!editInit) return null;
                            return (
                                <Formik
                                    initialValues={editInit}
                                    validationSchema={editGroupValidationSchema}
                                    onSubmit={handleSubmitEditGroup}
                                    enableReinitialize
                                >
                                    {({ values, errors, setFieldValue }) => (
                                        <Form>
                                            <Box marginBottom="26px">
                                                <Typography textAlign="left" fontSize="26px" lineHeight="36px" fontWeight="500" color="#4B465C">
                                                    Edit Level Group (Age {values.ageGroup.minAge} – {values.ageGroup.maxAge})
                                                </Typography>
                                            </Box>

                                            {/* ─── Age Group ──────────────── */}
                                            <Box display="flex" gap="16px" flexWrap="wrap" mb={2}>
                                                <Box flex={1}>
                                                    <Typography fontSize="13px" fontWeight="400" color="#4B465C">Min Age</Typography>
                                                    <Field style={{ width: "100%" }} name="ageGroup.minAge" type="number" as={TextField} inputProps={{ min: 0 }} />
                                                    <ErrorMessage component={FormikErrorText} name="ageGroup.minAge" />
                                                </Box>
                                                <Box flex={1}>
                                                    <Typography fontSize="13px" fontWeight="400" color="#4B465C">Max Age</Typography>
                                                    <Field style={{ width: "100%" }} name="ageGroup.maxAge" type="number" as={TextField} inputProps={{ min: 0 }} />
                                                    <ErrorMessage component={FormikErrorText} name="ageGroup.maxAge" />
                                                </Box>
                                            </Box>

                                            {/* ─── Has Questions toggle ────── */}
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={values.hasQuestions}
                                                        onChange={(e) => {
                                                            setFieldValue("hasQuestions", e.target.checked);
                                                            if (e.target.checked && !values.questions?.length) {
                                                                setFieldValue("questions", [{ questionText: "", answers: [{ text: "", stopQuestionnaire: false }] }]);
                                                            }
                                                            if (!e.target.checked) {
                                                                setFieldValue("questions", []);
                                                                values.scenarios.forEach((_, idx) => {
                                                                    setFieldValue(`scenarios.${idx}.answers`, []);
                                                                });
                                                            }
                                                        }}
                                                    />
                                                }
                                                label="Has Questions"
                                            />

                                            {/* ─── Questions Editor ────────── */}
                                            {values.hasQuestions && (
                                                <Box mt={2} mb={2}>
                                                    <Typography variant="h6" gutterBottom>Questions & Answers</Typography>
                                                    <QuestionsEditor basePath="questions" questions={values.questions} setFieldValue={setFieldValue} />
                                                </Box>
                                            )}

                                            {/* ─── Scenarios (Level mappings) */}
                                            <Box mt={3}>
                                                <Typography variant="h6" gutterBottom>Scenarios (Levels)</Typography>
                                                <FieldArray name="scenarios">
                                                    {({ push, remove }) => (
                                                        <Box>
                                                            {values.scenarios.map((scenario, sIndex) => (
                                                                <Box key={sIndex} border={1} borderColor="grey.300" borderRadius="10px" mt={2}>
                                                                    <Accordion defaultExpanded>
                                                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                                                            <Typography variant="subtitle1">
                                                                                {scenario.scenarioLevel || `Scenario ${sIndex + 1}`}
                                                                                {scenario.levelId && (
                                                                                    <Chip label={`#${scenario.levelId}`} size="small" sx={{ ml: 1 }} />
                                                                                )}
                                                                            </Typography>
                                                                        </AccordionSummary>
                                                                        <AccordionDetails>
                                                                            <Box display="flex" flexDirection="column" gap={2}>
                                                                                <Field
                                                                                    name={`scenarios.${sIndex}.scenarioLevel`}
                                                                                    as={TextField}
                                                                                    label="Level Name"
                                                                                    fullWidth
                                                                                />
                                                                                <ErrorMessage name={`scenarios.${sIndex}.scenarioLevel`} component={FormikErrorText} />

                                                                                {/* Answer selection per question */}
                                                                                {values.hasQuestions && values.questions?.map((question, qIndex) => {
                                                                                    if (!shouldShowQuestion(values.questions, qIndex, scenario.answers || [])) return null;
                                                                                    return (
                                                                                        <Field
                                                                                            key={qIndex}
                                                                                            name={`scenarios.${sIndex}.answers.${qIndex}`}
                                                                                            as={TextField}
                                                                                            select
                                                                                            label={question.questionText || `Question ${qIndex + 1}`}
                                                                                            fullWidth
                                                                                            value={scenario.answers?.[qIndex] || ""}
                                                                                            onChange={(e) => {
                                                                                                const newAnswers = [...(scenario.answers || [])];
                                                                                                newAnswers[qIndex] = e.target.value;
                                                                                                const selAns = question.answers.find((a) => a.text === e.target.value);
                                                                                                if (selAns?.stopQuestionnaire) {
                                                                                                    for (let i = qIndex + 1; i < newAnswers.length; i++) newAnswers[i] = "";
                                                                                                }
                                                                                                setFieldValue(`scenarios.${sIndex}.answers`, newAnswers);
                                                                                            }}
                                                                                        >
                                                                                            <MenuItem value="">Select Answer</MenuItem>
                                                                                            {question.answers?.map((ans, aIdx) => (
                                                                                                <MenuItem key={aIdx} value={ans.text} style={{ color: ans.stopQuestionnaire ? "#f44336" : "inherit", fontWeight: ans.stopQuestionnaire ? "bold" : "normal" }}>
                                                                                                    {ans.text}{ans.stopQuestionnaire && " (Stop)"}
                                                                                                </MenuItem>
                                                                                            ))}
                                                                                        </Field>
                                                                                    );
                                                                                })}
                                                                            </Box>
                                                                            <Box display="flex" justifyContent="end" mt={2}>
                                                                                <Button size="small" color="error" onClick={() => remove(sIndex)} startIcon={<Delete />}>
                                                                                    Remove Level
                                                                                </Button>
                                                                            </Box>
                                                                        </AccordionDetails>
                                                                    </Accordion>
                                                                </Box>
                                                            ))}
                                                            <Button
                                                                onClick={() => push({ levelId: null, scenarioLevel: "", answers: values.questions?.map(() => "") || [] })}
                                                                startIcon={<Add />}
                                                                sx={{ mt: 2 }}
                                                            >
                                                                Add Level to Group
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </FieldArray>
                                            </Box>

                                            <Box mt={3} display="flex" justifyContent="flex-end">
                                                <Button type="submit" variant="primary_active" disabled={Object.keys(errors).length > 0}>
                                                    Update Level Group
                                                </Button>
                                            </Box>
                                            {Object.keys(errors).length > 0 && (
                                                <Typography variant="body2" color="error" mt={1}>Please fix the validation errors above before submitting.</Typography>
                                            )}
                                        </Form>
                                    )}
                                </Formik>
                            );
                        })()}
                    </Box>
                </Box>
            </Modal>

            {/* ═══════════ Modal: Delete Level ═══════════ */}
            <Modal open={openDeleteLevel.open} onClose={() => setOpenDeleteLevel({ open: false, data: null })}>
                <Box sx={modalStyleOverflow}>
                    <Box position="absolute" top="2px" right="2px">
                        <ButtonSquare action="close" onClick={() => setOpenDeleteLevel({ open: false, data: null })} />
                    </Box>
                    <Box mb={2}>
                        <Typography textAlign="center" fontSize="26px" lineHeight="36px" fontWeight="500" color="#4B465C">
                            Delete Level
                        </Typography>
                    </Box>
                    <Box>
                        <Typography textAlign="center" fontSize="22px" lineHeight="32px" fontWeight="500" color="#4B465C">
                            Are you sure you want to delete level {openDeleteLevel?.data?.name}?
                        </Typography>
                    </Box>
                    <Box mt={2} display="flex" justifyContent="center" gap="16px">
                        <Button variant="primary_active" onClick={() => handleDeleteLevel(openDeleteLevel?.data?.id)}>Delete</Button>
                        <Button variant="primary_inactive" onClick={() => setOpenDeleteLevel({ open: false, data: null })}>Cancel</Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}

// ─── Reusable Questions & Answers Editor ──────────────────────
function QuestionsEditor({ basePath, questions, setFieldValue }) {
    return (
        <FieldArray name={basePath}>
            {({ push: pushQuestion, remove: removeQuestion }) => (
                <Box mt={2}>
                    {questions?.map((question, qIndex) => (
                        <Box key={qIndex} border={1} borderColor="grey.300" borderRadius="10px" p={2} mt={2}>
                            <Field
                                name={`${basePath}.${qIndex}.questionText`}
                                as={TextField}
                                label={`Question ${qIndex + 1}`}
                                fullWidth
                            />
                            <ErrorMessage name={`${basePath}.${qIndex}.questionText`} component={FormikErrorText} />

                            <FieldArray name={`${basePath}.${qIndex}.answers`}>
                                {({ push: pushAnswer, remove: removeAnswer }) => (
                                    <Box mt={2}>
                                        {question.answers?.map((answer, aIndex) => (
                                            <Box key={aIndex} border={1} borderColor="grey.200" borderRadius="5px" p={2} mb={2}>
                                                <Box display="flex" alignItems="center" gap={2} mb={1}>
                                                    <Field
                                                        name={`${basePath}.${qIndex}.answers.${aIndex}.text`}
                                                        as={TextField}
                                                        label={`Answer ${aIndex + 1}`}
                                                        fullWidth
                                                    />
                                                    <ButtonSquare action="delete_red" onClick={() => removeAnswer(aIndex)} />
                                                </Box>
                                                <ErrorMessage name={`${basePath}.${qIndex}.answers.${aIndex}.text`} component={FormikErrorText} />
                                                <Field name={`${basePath}.${qIndex}.answers.${aIndex}.stopQuestionnaire`}>
                                                    {({ field }) => (
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    {...field}
                                                                    checked={field.value || false}
                                                                    onChange={(e) => setFieldValue(`${basePath}.${qIndex}.answers.${aIndex}.stopQuestionnaire`, e.target.checked)}
                                                                />
                                                            }
                                                            label="This answer stops the questionnaire"
                                                        />
                                                    )}
                                                </Field>
                                            </Box>
                                        ))}
                                        <Box display="flex" justifyContent="space-between" mt={2}>
                                            <Button size="small" onClick={() => pushAnswer({ text: "", stopQuestionnaire: false })} startIcon={<Add />}>Add Answer</Button>
                                            <Button size="small" color="error" onClick={() => removeQuestion(qIndex)} startIcon={<Delete />}>Remove Question</Button>
                                        </Box>
                                    </Box>
                                )}
                            </FieldArray>
                        </Box>
                    ))}
                    <Box display="flex" justifyContent="space-between" mt={2}>
                        <Button size="small" onClick={() => pushQuestion({ questionText: "", answers: [{ text: "", stopQuestionnaire: false }] })} startIcon={<Add />}>Add Question</Button>
                    </Box>
                </Box>
            )}
        </FieldArray>
    );
}

export default Levels;
